import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { adminListCourses } from "@/services/adminApi";
import axios from "axios";
import {
  Users, CheckCircle2, Clock, Search, Filter, Download,
  ChevronUp, ChevronDown, BookOpen, Award, RefreshCw, AlertCircle
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL + "/api/admin";
const adminHeaders = () => ({ "X-Admin-Password": sessionStorage.getItem("nt_admin_token") || "" });

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("hu-HU", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function MiniBar({ value, max, color = "#A1E1B9" }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-2">
      <div
        style={{
          width: 64, height: 8, background: "#e8e0d5",
          borderRadius: 99, border: "1px solid #242220", overflow: "hidden",
        }}
      >
        <div style={{ width: `${pct}%`, background: color, height: "100%", borderRadius: 99 }} />
      </div>
      <span className="text-xs font-bold" style={{ color: "#242220", minWidth: 32 }}>{pct}%</span>
    </div>
  );
}

function StatusBadge({ passed, attempts }) {
  if (passed) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold"
        style={{ background: "#A1E1B9", border: "1.5px solid #242220", borderRadius: 99, color: "#242220" }}>
        <CheckCircle2 size={11} strokeWidth={2.5} /> Teljesített
      </span>
    );
  }
  if (attempts > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold"
        style={{ background: "#FF9456", border: "1.5px solid #242220", borderRadius: 99, color: "#242220" }}>
        <AlertCircle size={11} strokeWidth={2.5} /> Folyamatban ({attempts} kísérlet)
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold"
      style={{ background: "#FBD556", border: "1.5px solid #242220", borderRadius: 99, color: "#242220" }}>
      <Clock size={11} strokeWidth={2.5} /> Elkezdett
    </span>
  );
}

function SortHeader({ label, field, sortField, sortDir, onSort }) {
  const active = sortField === field;
  return (
    <th
      className="text-left px-4 py-3 text-xs font-bold cursor-pointer select-none"
      style={{ color: "#242220", opacity: active ? 1 : 0.6, whiteSpace: "nowrap" }}
      onClick={() => onSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        {active ? (
          sortDir === "asc" ? <ChevronUp size={12} strokeWidth={2.5} /> : <ChevronDown size={12} strokeWidth={2.5} />
        ) : (
          <ChevronDown size={12} strokeWidth={2} style={{ opacity: 0.3 }} />
        )}
      </span>
    </th>
  );
}

function exportToCSV(rows) {
  const headers = ["Felhasználó ID", "Kurzus neve", "Azonosító", "Leckék", "Haladás %", "Vizsga", "Eredmény %", "Kísérletek", "Teljesítés dátuma"];
  const csvRows = [
    headers.join(";"),
    ...rows.map(r => [
      r.user_id,
      r.course_name,
      r.course_identifier,
      `${r.lessons_completed}/${r.total_lessons}`,
      r.lesson_progress_pct,
      r.exam_passed ? "Teljesített" : (r.exam_attempts > 0 ? "Folyamatban" : "Elkezdett"),
      r.exam_score ?? "",
      r.exam_attempts,
      r.completed_at ? formatDate(r.completed_at) : "",
    ].join(";"))
  ];
  const blob = new Blob(["\uFEFF" + csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url;
  a.download = `naturtrak_lms_haladás_${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function AdminProgressPage() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchUser, setSearchUser] = useState("");
  const [filterCourse, setFilterCourse] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [sortField, setSortField] = useState("completed_at");
  const [sortDir, setSortDir] = useState("desc");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCourse) params.course_identifier = filterCourse;
      if (filterStatus) params.status = filterStatus;
      const [pRes, cRes] = await Promise.all([
        axios.get(`${BASE}/progress`, { headers: adminHeaders(), params }),
        adminListCourses(),
      ]);
      setRecords(pRes.data);
      setCourses(cRes.data);
    } catch {
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  }, [filterCourse, filterStatus, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  // Filter + sort
  const filtered = records
    .filter(r => !searchUser || String(r.user_id).includes(searchUser))
    .sort((a, b) => {
      let av = a[sortField], bv = b[sortField];
      if (av == null) av = "";
      if (bv == null) bv = "";
      if (typeof av === "boolean") av = av ? 1 : 0;
      if (typeof bv === "boolean") bv = bv ? 1 : 0;
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });

  // Summary stats
  const totalCompleted = records.filter(r => r.exam_passed).length;
  const totalInProgress = records.filter(r => !r.exam_passed && r.exam_attempts > 0).length;
  const uniqueUsers = new Set(records.map(r => r.user_id)).size;
  const avgScore = records.filter(r => r.exam_score != null).reduce((s, r, _, a) => s + r.exam_score / a.length, 0);

  return (
    <AdminLayout>
      <div data-testid="admin-progress-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: "#242220" }}>Felhasználói Haladás</h1>
            <p className="text-sm" style={{ color: "#242220", opacity: 0.5 }}>Ki, mikor teljesítette melyik kurzust</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={load}
              className="flex items-center gap-1.5 px-4 py-2.5 font-bold text-sm"
              style={{ background: "#FDF5EB", borderRadius: "20px", border: "1.5px solid #242220", boxShadow: "2px 2px 0 #242220", color: "#242220", cursor: "pointer" }}
              data-testid="refresh-btn"
            >
              <RefreshCw size={14} strokeWidth={2.5} /> Frissítés
            </button>
            <button
              onClick={() => exportToCSV(filtered)}
              className="flex items-center gap-1.5 px-4 py-2.5 font-bold text-sm transition-all active:translate-x-[1px] active:translate-y-[1px]"
              style={{ background: "#A1E1B9", borderRadius: "20px", border: "1.5px solid #242220", boxShadow: "3px 3px 0 #242220", color: "#242220", cursor: "pointer" }}
              data-testid="export-csv-btn"
            >
              <Download size={14} strokeWidth={2.5} /> CSV Export
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Egyedi felhasználók", value: uniqueUsers, color: "#FBD556", icon: Users },
            { label: "Teljesített kurzus", value: totalCompleted, color: "#A1E1B9", icon: CheckCircle2 },
            { label: "Folyamatban", value: totalInProgress, color: "#FF9456", icon: Clock },
            { label: "Átlagos vizsga eredmény", value: records.filter(r => r.exam_score != null).length > 0 ? `${Math.round(avgScore)}%` : "—", color: "#FBD556", icon: Award },
          ].map((s) => (
            <div key={s.label} className="p-4"
              style={{ background: s.color, borderRadius: "18px", border: "1.5px solid #242220", boxShadow: "3px 3px 0 #242220" }}>
              <s.icon size={18} strokeWidth={2.5} color="#242220" />
              <p className="text-2xl font-bold mt-1.5" style={{ color: "#242220" }}>{s.value}</p>
              <p className="text-xs font-bold" style={{ color: "#242220", opacity: 0.7 }}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div
          className="flex gap-3 flex-wrap mb-5 p-4"
          style={{ background: "#fff", borderRadius: "16px", border: "1.5px solid #242220", boxShadow: "3px 3px 0 #242220" }}
        >
          <div className="flex items-center gap-2 flex-1 min-w-48">
            <Search size={15} strokeWidth={2.5} color="#242220" style={{ opacity: 0.4, flexShrink: 0 }} />
            <input
              value={searchUser}
              onChange={(e) => setSearchUser(e.target.value)}
              placeholder="Keresés felhasználó ID alapján..."
              className="flex-1 text-sm outline-none"
              style={{ background: "transparent", border: "none", color: "#242220", fontFamily: "Quicksand, sans-serif" }}
              data-testid="search-user-input"
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter size={14} strokeWidth={2.5} color="#242220" style={{ opacity: 0.4 }} />
            <select
              value={filterCourse}
              onChange={(e) => setFilterCourse(e.target.value)}
              className="text-sm font-bold outline-none"
              style={{ background: "#FDF5EB", border: "1.5px solid #242220", borderRadius: 12, padding: "6px 10px", color: "#242220", fontFamily: "Quicksand, sans-serif", cursor: "pointer" }}
              data-testid="filter-course-select"
            >
              <option value="">Összes kurzus</option>
              {courses.map(c => <option key={c.identifier} value={c.identifier}>{c.name}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="text-sm font-bold outline-none"
              style={{ background: "#FDF5EB", border: "1.5px solid #242220", borderRadius: 12, padding: "6px 10px", color: "#242220", fontFamily: "Quicksand, sans-serif", cursor: "pointer" }}
              data-testid="filter-status-select"
            >
              <option value="">Összes státusz</option>
              <option value="completed">Teljesített</option>
              <option value="in_progress">Folyamatban</option>
            </select>
          </div>

          {(searchUser || filterCourse || filterStatus) && (
            <button
              onClick={() => { setSearchUser(""); setFilterCourse(""); setFilterStatus(""); }}
              className="text-xs font-bold px-3 py-1.5"
              style={{ background: "#FDECEA", border: "1.5px solid #D32F2F", borderRadius: 12, color: "#D32F2F", cursor: "pointer" }}
              data-testid="clear-filters-btn"
            >
              Szűrők törlése
            </button>
          )}
        </div>

        {/* Table */}
        <div
          style={{
            background: "#fff", borderRadius: "20px",
            border: "1.5px solid #242220", boxShadow: "4px 4px 0 #242220",
            overflow: "hidden",
          }}
        >
          {loading ? (
            <div className="text-center py-16" style={{ color: "#242220", opacity: 0.4 }}>
              <p className="font-bold">Betöltés...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16" data-testid="no-progress-msg">
              <BookOpen size={40} strokeWidth={1.5} color="#242220" style={{ opacity: 0.2, margin: "0 auto 12px" }} />
              <p className="font-bold" style={{ color: "#242220" }}>
                {records.length === 0 ? "Még nincs haladási adat" : "Nincs a szűrőknek megfelelő eredmény"}
              </p>
              <p className="text-sm mt-1" style={{ color: "#242220", opacity: 0.4 }}>
                {records.length === 0
                  ? "Miután a felhasználók elkezdik a kurzusokat, az adatok itt jelennek meg."
                  : "Próbálj más szűrési feltételeket."}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#FDF5EB", borderBottom: "1.5px solid #242220" }}>
                    <SortHeader label="Felhasználó" field="user_id" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortHeader label="Kurzus neve" field="course_name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <th className="text-left px-4 py-3 text-xs font-bold" style={{ color: "#242220", opacity: 0.6 }}>Leckék</th>
                    <SortHeader label="Státusz" field="exam_passed" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortHeader label="Vizsga %" field="exam_score" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortHeader label="Teljesítés dátuma" field="completed_at" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, idx) => (
                    <tr
                      key={`${r.user_id}-${r.course_identifier}-${idx}`}
                      style={{ borderBottom: "1px solid #f0e8dc" }}
                      data-testid={`progress-row-${idx}`}
                    >
                      {/* User */}
                      <td className="px-4 py-3">
                        <div
                          className="inline-flex items-center gap-2 px-3 py-1.5"
                          style={{ background: "#FBD556", borderRadius: 99, border: "1.5px solid #242220" }}
                        >
                          <Users size={12} strokeWidth={2.5} color="#242220" />
                          <span className="text-sm font-bold" style={{ color: "#242220" }}>#{r.user_id}</span>
                        </div>
                      </td>

                      {/* Course */}
                      <td className="px-4 py-3">
                        <p className="text-sm font-bold" style={{ color: "#242220", maxWidth: 200 }}>{r.course_name}</p>
                        <p className="text-xs" style={{ color: "#242220", opacity: 0.4 }}>{r.course_identifier}</p>
                      </td>

                      {/* Lessons */}
                      <td className="px-4 py-3">
                        <p className="text-xs font-bold mb-1" style={{ color: "#242220" }}>
                          {r.lessons_completed}/{r.total_lessons}
                        </p>
                        <MiniBar value={r.lessons_completed} max={r.total_lessons} />
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <StatusBadge passed={r.exam_passed} attempts={r.exam_attempts} />
                      </td>

                      {/* Score */}
                      <td className="px-4 py-3">
                        {r.exam_score != null ? (
                          <div className="flex items-center gap-2">
                            <span
                              className="text-sm font-bold"
                              style={{ color: r.exam_score >= 70 ? "#242220" : "#D32F2F" }}
                            >
                              {r.exam_score}%
                            </span>
                            <MiniBar
                              value={r.exam_score}
                              max={100}
                              color={r.exam_score >= 70 ? "#A1E1B9" : "#FDECEA"}
                            />
                          </div>
                        ) : (
                          <span className="text-xs" style={{ color: "#242220", opacity: 0.3 }}>—</span>
                        )}
                      </td>

                      {/* Date */}
                      <td className="px-4 py-3">
                        {r.exam_passed && r.completed_at ? (
                          <div>
                            <p className="text-xs font-bold" style={{ color: "#242220" }}>
                              {formatDate(r.completed_at)}
                            </p>
                          </div>
                        ) : r.last_exam_at ? (
                          <p className="text-xs" style={{ color: "#242220", opacity: 0.5 }}>
                            Utolsó kísérlet: {formatDate(r.last_exam_at)}
                          </p>
                        ) : (
                          <span className="text-xs" style={{ color: "#242220", opacity: 0.3 }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          {filtered.length > 0 && (
            <div
              className="px-4 py-3 flex items-center justify-between"
              style={{ borderTop: "1px solid #f0e8dc", background: "#FAFAFA" }}
            >
              <p className="text-xs font-bold" style={{ color: "#242220", opacity: 0.5 }}>
                {filtered.length} bejegyzés megjelenítve{records.length !== filtered.length ? ` (összes: ${records.length})` : ""}
              </p>
              <button
                onClick={() => exportToCSV(filtered)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold"
                style={{ background: "#FDF5EB", borderRadius: 12, border: "1.5px solid #242220", color: "#242220", cursor: "pointer" }}
              >
                <Download size={12} strokeWidth={2.5} /> CSV
              </button>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
